import { useState } from 'react'
import { NavLink, Routes, Route, useNavigate } from 'react-router-dom'
import {
  Gauge,
  Building2,
  Gavel,
  ListChecks,
  BarChart3,
  LogOut,
  TrendingUp,
  ChevronRight,
  BadgeCheck,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { toast } from '@/components/ui/Toast'
import { STRIPE } from '@/data/mockData'

const C = {
  teal: '#0F5C5C',
  tealDk: '#153D3D',
  terra: '#D9785B',
  amber: '#D3A34A',
  grayBg: '#F1F3F2',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  success: '#3E8B63',
  warn: '#D89932',
  error: '#C85C5C',
  live: '#D85F5F',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

// Datos de plataforma (reemplazar con Supabase)
const STATS = [
  { icon: '👥', val: '12,480', label: 'Usuarios activos', delta: '+6%', color: C.teal },
  { icon: '🏪', val: '342', label: 'Negocios activos', delta: '+4 nuevos', color: C.terra },
  { icon: '📋', val: '1,208', label: 'Publicaciones activas', delta: '+38', color: C.amber },
  { icon: '📡', val: '86', label: 'Actividades en vivo', delta: '', color: C.live },
  { icon: '🏷️', val: '124', label: 'Promociones activas', delta: '', color: C.terra },
  { icon: '📅', val: '58', label: 'Eventos futuros', delta: '', color: C.teal },
  { icon: '📸', val: '3,910', label: 'Fotos subidas', delta: '+210', color: C.amber },
  { icon: '🚩', val: '17', label: 'Reportes pendientes', delta: '', color: C.error },
]

const REPORTS = [
  {
    type: 'Publicación',
    desc: 'Foto con rostro sin consentimiento',
    source: 'Club Neón Bajo',
    severity: 'high',
    time: 'hace 2 h',
  },
  {
    type: 'Comentario',
    desc: 'Lenguaje ofensivo hacia usuarios',
    source: '@jdoe',
    severity: 'high',
    time: 'hace 3 h',
  },
  {
    type: 'Promoción',
    desc: 'Información desactualizada y engañosa',
    source: 'Trattoria Lucia',
    severity: 'med',
    time: 'hace 5 h',
  },
  {
    type: 'Contenido',
    desc: 'Posible spam y publicidad encubierta',
    source: 'Refugio Creativo',
    severity: 'med',
    time: 'hace 6 h',
  },
  {
    type: 'Usuario',
    desc: 'Múltiples reportes por comportamiento',
    source: '@spam_user',
    severity: 'high',
    time: 'hace 8 h',
  },
]

const BUSINESSES = [
  {
    name: 'Cielo Rooftop',
    cat: 'Rooftop',
    zone: 'Circunvalar',
    verified: true,
    plan: 'Profesional',
  },
  { name: 'Trattoria Lucia', cat: 'Restaurante', zone: 'Pinares', verified: true, plan: 'Gratis' },
  { name: 'Grano & Pluma', cat: 'Café', zone: 'Pinares', verified: true, plan: 'Profesional' },
  { name: 'Mestizo Gastrobar', cat: 'Gastrobar', zone: 'Álamos', verified: false, plan: 'Gratis' },
  { name: 'Club Neón Bajo', cat: 'Discoteca', zone: 'Cerritos', verified: true, plan: 'Destacado' },
  {
    name: 'Galería Intervalo',
    cat: 'Galería',
    zone: 'Circunvalar',
    verified: false,
    plan: 'Gratis',
  },
]

// ─── Shell del admin ──────────────────────────────────────────────────────────
function AdminShell({
  children,
  pendingRequests = 0,
}: {
  children: React.ReactNode
  pendingRequests?: number
}) {
  const { signOut } = useAuth()
  const navigate = useNavigate()

  const navItems = [
    { to: '/admin', icon: <Gauge size={20} />, label: 'Resumen' },
    {
      to: '/admin/registrations',
      icon: <Building2 size={20} />,
      label: 'Solicitudes',
      badge: pendingRequests,
    },
    { to: '/admin/moderation', icon: <Gavel size={20} />, label: 'Moderación', badge: 17 },
    { to: '/admin/manage', icon: <ListChecks size={20} />, label: 'Gestión' },
    { to: '/admin/metrics', icon: <BarChart3 size={20} />, label: 'Métricas' },
  ]

  return (
    <div className="admin-shell-wrap">
      {/* Topbar oscuro */}
      <header
        style={{
          height: 60,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          background: C.tealDk,
          padding: '0 20px',
          gap: 16,
          zIndex: 50,
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}
          onClick={() => navigate('/admin')}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50% 50% 50% 4px',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: 16, color: C.teal }}>⚡</span>
          </div>
          <span
            style={{
              fontFamily: FH,
              fontWeight: 800,
              fontSize: 20,
              color: '#fff',
              letterSpacing: '-0.02em',
            }}
          >
            Párchala
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: C.amber,
              background: 'rgba(211,163,74,.18)',
              borderRadius: 6,
              padding: '3px 8px',
            }}
          >
            Admin
          </span>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {pendingRequests > 0 && (
            <button
              onClick={() => navigate('/admin/registrations')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '8px 14px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,.2)',
                background: 'rgba(255,255,255,.1)',
                color: '#fff',
                fontFamily: FB,
                fontWeight: 700,
                fontSize: 13.5,
                cursor: 'pointer',
              }}
            >
              <Building2 size={16} /> {pendingRequests} solicitudes
              <span
                style={{
                  background: C.terra,
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: 99,
                }}
              >
                {pendingRequests}
              </span>
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 99,
                background: `${C.amber}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
              }}
            >
              🛡️
            </div>
            <span style={{ color: 'rgba(255,255,255,.85)', fontSize: 13.5, fontWeight: 600 }}>
              admin
            </span>
          </div>
          <button
            onClick={signOut}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 13px',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,.2)',
              background: 'rgba(255,255,255,.1)',
              color: 'rgba(255,255,255,.8)',
              fontFamily: FB,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <LogOut size={15} /> Salir
          </button>
        </div>
      </header>

      <div className="admin-body">
        {/* Sidebar oscuro */}
        <nav className="admin-sidebar" style={{ background: C.tealDk, padding: '14px 10px' }}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/admin'}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderRadius: 12,
                textDecoration: 'none',
                marginBottom: 3,
                background: isActive ? 'rgba(255,255,255,.12)' : 'transparent',
                color: isActive ? '#fff' : 'rgba(255,255,255,.65)',
                fontFamily: FB,
                fontWeight: isActive ? 700 : 500,
                fontSize: 14.5,
              })}
            >
              {item.icon}
              {item.label}
              {item.badge ? (
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#fff',
                    background: C.terra,
                    borderRadius: 99,
                    padding: '2px 7px',
                  }}
                >
                  {item.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
          <div
            style={{
              marginTop: 'auto',
              paddingTop: 12,
              borderTop: '1px solid rgba(255,255,255,.12)',
            }}
          >
            <button
              onClick={signOut}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderRadius: 12,
                border: 'none',
                background: 'transparent',
                color: 'rgba(255,255,255,.5)',
                fontFamily: FB,
                fontWeight: 500,
                fontSize: 14.5,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              <LogOut size={20} /> Cerrar sesión
            </button>
          </div>
        </nav>

        <main className="admin-main">{children}</main>
      </div>
    </div>
  )
}

// ─── Métrica ──────────────────────────────────────────────────────────────────
function MetricCard({
  icon,
  val,
  label,
  delta,
  color = C.teal,
}: {
  icon: string
  val: string
  label: string
  delta?: string
  color?: string
}) {
  return (
    <div
      style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          {icon}
        </div>
        {delta && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.success,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <TrendingUp size={12} /> {delta}
          </span>
        )}
      </div>
      <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 24, marginBottom: 2 }}>{val}</div>
      <div style={{ fontSize: 12.5, color: C.ink2 }}>{label}</div>
    </div>
  )
}

// ─── Bar chart ────────────────────────────────────────────────────────────────
function BarChart() {
  const data = [42, 55, 48, 67, 72, 61, 88]
  const days = ['L', 'M', 'M', 'J', 'V', 'S', 'D']
  const max = Math.max(...data)
  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: '16px 14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
        {data.map((d, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              height: '100%',
              justifyContent: 'flex-end',
            }}
          >
            <div
              style={{
                width: '100%',
                height: `${(d / max) * 100}%`,
                background: i === 6 ? C.teal : `${C.teal}40`,
                borderRadius: '6px 6px 0 0',
                minHeight: 6,
              }}
            />
            <span style={{ fontSize: 11, color: C.ink2, fontWeight: 600 }}>{days[i]}</span>
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 12,
          fontSize: 12,
          color: C.ink2,
        }}
      >
        <span>Usuarios nuevos esta semana</span>
        <span style={{ fontWeight: 700, color: C.success }}>+18% vs. anterior</span>
      </div>
    </div>
  )
}

// ─── RESUMEN ──────────────────────────────────────────────────────────────────
function AdminOverview() {
  const alerts = [
    {
      icon: '🏢',
      text: 'Solicitudes de registro pendientes',
      color: C.teal,
      to: '/admin/registrations',
    },
    {
      icon: '🚩',
      text: '17 reportes pendientes de revisión',
      color: C.error,
      to: '/admin/moderation',
    },
    { icon: '✅', text: '8 negocios esperando verificación', color: C.warn, to: '/admin/manage' },
    {
      icon: '👤',
      text: '3 apelaciones de usuarios bloqueados',
      color: C.terra,
      to: '/admin/moderation',
    },
  ]
  const navigate = useNavigate()

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 28px 60px' }}>
      <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26, marginBottom: 4 }}>
        Panel de control
      </div>
      <div style={{ fontSize: 14, color: C.ink2, marginBottom: 24 }}>
        Pereira, Risaralda ·{' '}
        {new Date().toLocaleDateString('es-PA', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}
      </div>

      <div className="admin-4" style={{ marginBottom: 14 }}>
        {STATS.slice(0, 4).map((s) => (
          <MetricCard
            key={s.label}
            icon={s.icon}
            val={s.val}
            label={s.label}
            delta={s.delta || undefined}
            color={s.color}
          />
        ))}
      </div>
      <div className="admin-4" style={{ marginBottom: 28 }}>
        {STATS.slice(4).map((s) => (
          <MetricCard
            key={s.label}
            icon={s.icon}
            val={s.val}
            label={s.label}
            delta={s.delta || undefined}
            color={s.color}
          />
        ))}
      </div>

      <div className="admin-two-col">
        <div>
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 17, marginBottom: 14 }}>
            Requiere atención
          </div>
          {alerts.map((a, i) => (
            <button
              key={i}
              onClick={() => navigate(a.to)}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                padding: 14,
                background: '#fff',
                border: `1px solid ${C.border}`,
                borderRadius: 13,
                marginBottom: 10,
                cursor: 'pointer',
                fontFamily: FB,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 11,
                  background: `${a.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: 20,
                }}
              >
                {a.icon}
              </div>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{a.text}</div>
              <ChevronRight size={18} color={C.ink2} />
            </button>
          ))}
        </div>
        <div>
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 17, marginBottom: 14 }}>
            Crecimiento semanal
          </div>
          <BarChart />
        </div>
      </div>
    </div>
  )
}

// ─── SOLICITUDES DE REGISTRO ──────────────────────────────────────────────────
function AdminRegistrations() {
  // Lee del localStorage (guardado por login.html / RegisterBusinessForm)
  const raw = localStorage.getItem('px_pending_biz') ?? '[]'
  const [requests, setRequests] = useState<
    {
      id: string
      name: string
      cat: string
      zone: string
      desc: string
      contact: string
      email: string
      ig?: string
      status: string
      sentAt: string
    }[]
  >(() => {
    try {
      return JSON.parse(raw)
    } catch {
      return []
    }
  })

  const updateStatus = (id: string, status: 'approved' | 'rejected') => {
    const updated = requests.map((r) => (r.id === id ? { ...r, status } : r))
    setRequests(updated)
    localStorage.setItem('px_pending_biz', JSON.stringify(updated))
    toast.success(status === 'approved' ? 'Negocio aprobado ✓' : 'Solicitud rechazada')
  }

  const pending = requests.filter((r) => r.status === 'pending')
  const approved = requests.filter((r) => r.status === 'approved')
  const rejected = requests.filter((r) => r.status === 'rejected')

  const Card = ({ r }: { r: (typeof requests)[0] }) => (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: 20,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 18, marginBottom: 3 }}>
            {r.name}
          </div>
          <div
            style={{ fontSize: 13.5, color: C.ink2, display: 'flex', gap: 10, flexWrap: 'wrap' }}
          >
            <span>🏷️ {r.cat}</span>
            <span>📍 {r.zone}</span>
            <span>
              🕐{' '}
              {new Date(r.sentAt).toLocaleDateString('es-PA', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        </div>
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: 8,
            color: r.status === 'pending' ? C.warn : r.status === 'approved' ? C.success : C.error,
            background:
              r.status === 'pending'
                ? `${C.warn}15`
                : r.status === 'approved'
                  ? `${C.success}15`
                  : `${C.error}15`,
          }}
        >
          {r.status === 'pending'
            ? 'Pendiente'
            : r.status === 'approved'
              ? 'Aprobado'
              : 'Rechazado'}
        </span>
      </div>

      {r.desc && (
        <div
          style={{
            background: C.grayBg,
            borderRadius: 12,
            padding: 14,
            marginBottom: 14,
            fontSize: 13.5,
            color: C.ink,
            lineHeight: 1.55,
          }}
        >
          {r.desc}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 10,
          marginBottom: r.status === 'pending' ? 14 : 0,
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.ink2, marginBottom: 3 }}>
            WHATSAPP
          </div>
          <div style={{ fontSize: 13.5 }}>{r.contact || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.ink2, marginBottom: 3 }}>
            CORREO
          </div>
          <div style={{ fontSize: 13.5 }}>{r.email || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.ink2, marginBottom: 3 }}>
            INSTAGRAM
          </div>
          <div style={{ fontSize: 13.5 }}>{r.ig || '—'}</div>
        </div>
      </div>

      {r.status === 'pending' && (
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => updateStatus(r.id, 'approved')}
            style={{
              flex: 1,
              padding: 11,
              borderRadius: 11,
              border: 'none',
              background: C.teal,
              color: '#fff',
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
            }}
          >
            <BadgeCheck size={16} /> Aprobar registro
          </button>
          <button
            onClick={() => updateStatus(r.id, 'rejected')}
            style={{
              padding: '11px 18px',
              borderRadius: 11,
              border: `1px solid ${C.error}`,
              background: '#fff',
              color: C.error,
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Rechazar
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 28px 60px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26 }}>Solicitudes de registro</div>
        <div style={{ display: 'flex', gap: 14 }}>
          {[
            ['⏳', 'pendientes', pending.length, C.warn],
            ['✅', 'aprobadas', approved.length, C.success],
            ['❌', 'rechazadas', rejected.length, C.ink2],
          ].map(([_emoji, label, count, color]) => (
            <span
              key={label as string}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 13,
                fontWeight: 700,
                color: color as string,
              }}
            >
              <span
                style={{ width: 8, height: 8, borderRadius: 99, background: color as string }}
              />
              {count as number} {label as string}
            </span>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 14.5, color: C.ink2, marginBottom: 24 }}>
        Los negocios se registran desde el formulario de login. Revisa cada solicitud antes de
        aprobar.
      </div>

      {requests.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '80px 0',
            background: '#fff',
            borderRadius: 18,
            border: `1px solid ${C.border}`,
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏢</div>
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
            No hay solicitudes todavía
          </div>
          <div style={{ fontSize: 14, color: C.ink2, lineHeight: 1.5 }}>
            Cuando un negocio envíe su solicitud desde el login aparecerá aquí.
          </div>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  fontFamily: FH,
                  fontWeight: 700,
                  fontSize: 17,
                  marginBottom: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: C.warn,
                }}
              >
                ⏳ Pendientes de revisión ({pending.length})
              </div>
              {pending.map((r) => (
                <Card key={r.id} r={r} />
              ))}
            </div>
          )}
          {approved.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  fontFamily: FH,
                  fontWeight: 700,
                  fontSize: 17,
                  marginBottom: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: C.success,
                }}
              >
                ✅ Aprobadas ({approved.length})
              </div>
              {approved.map((r) => (
                <Card key={r.id} r={r} />
              ))}
            </div>
          )}
          {rejected.length > 0 && (
            <div>
              <div
                style={{
                  fontFamily: FH,
                  fontWeight: 700,
                  fontSize: 17,
                  marginBottom: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: C.ink2,
                }}
              >
                ❌ Rechazadas ({rejected.length})
              </div>
              {rejected.map((r) => (
                <Card key={r.id} r={r} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─── MODERACIÓN ───────────────────────────────────────────────────────────────
function AdminModeration() {
  const [filter, setFilter] = useState('Todos')
  const filters = ['Todos', 'Reportes', 'Publicaciones', 'Comentarios', 'Usuarios']
  const sevColor = (s: string) => (s === 'high' ? C.error : s === 'med' ? C.warn : C.ink2)
  const actions = ['Aprobar', 'Ocultar', 'Eliminar', 'Advertir', 'Difuminar']

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 28px 60px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26 }}>Moderación</div>
          <div style={{ fontSize: 14, color: C.ink2, marginTop: 2 }}>
            17 elementos en cola de revisión
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          marginBottom: 20,
        }}
      >
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              padding: '8px 14px',
              borderRadius: 99,
              flexShrink: 0,
              border: `1px solid ${filter === f ? C.teal : C.border}`,
              background: filter === f ? C.teal : '#fff',
              color: filter === f ? '#fff' : C.ink,
              fontFamily: FB,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {REPORTS.map((r, i) => (
        <div
          key={i}
          style={{
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            padding: '18px 20px',
            marginBottom: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                color: '#fff',
                background: sevColor(r.severity),
                borderRadius: 7,
                padding: '3px 9px',
              }}
            >
              {r.severity === 'high' ? 'Alta' : 'Media'} · {r.type}
            </span>
            <span style={{ fontSize: 12.5, color: C.ink2 }}>· {r.source}</span>
            <span style={{ fontSize: 12.5, color: C.ink2, marginLeft: 'auto' }}>{r.time}</span>
          </div>
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
            {r.desc}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {actions.map((a) => (
              <button
                key={a}
                onClick={() => toast.info(`${a} aplicado`)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 9,
                  cursor: 'pointer',
                  fontFamily: FB,
                  fontWeight: 700,
                  fontSize: 13,
                  border: `1px solid ${a === 'Aprobar' ? C.success : a === 'Eliminar' ? C.error : C.border}`,
                  background: a === 'Aprobar' ? C.success : a === 'Eliminar' ? C.error : '#fff',
                  color: a === 'Aprobar' || a === 'Eliminar' ? '#fff' : C.ink,
                }}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── GESTIÓN ──────────────────────────────────────────────────────────────────
function AdminManage() {
  const [tab, setTab] = useState('Negocios')
  const tabs = ['Negocios', 'Verificaciones', 'Suscripciones']

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 28px 60px' }}>
      <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26, marginBottom: 20 }}>Gestión</div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginBottom: 24 }}>
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '12px 18px',
              border: 'none',
              marginBottom: -1,
              borderBottom: `2.5px solid ${tab === t ? C.teal : 'transparent'}`,
              background: 'transparent',
              color: tab === t ? C.teal : C.ink2,
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      <div
        style={{
          background: '#fff',
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          overflow: 'hidden',
        }}
      >
        {(tab === 'Negocios' || tab === 'Verificaciones') && (
          <>
            <div
              className="act-table-hdr"
              style={{
                padding: '12px 20px',
                borderBottom: `1px solid ${C.border}`,
                fontSize: 12,
                fontWeight: 700,
                color: C.ink2,
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}
            >
              <span>Negocio</span>
              <span>Categoría</span>
              <span>Zona</span>
              <span>Plan</span>
              <span style={{ textAlign: 'right' }}>Acciones</span>
            </div>
            {(tab === 'Verificaciones' ? BUSINESSES.filter((b) => !b.verified) : BUSINESSES).map(
              (b, i) => (
                <div
                  key={i}
                  className="act-table-hdr"
                  style={{
                    padding: '14px 20px',
                    borderBottom: i < BUSINESSES.length - 1 ? `1px solid ${C.border}` : 'none',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: STRIPE(C.teal, '#17706f'),
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      🏪
                    </div>
                    <div>
                      <div
                        style={{
                          fontFamily: FH,
                          fontWeight: 700,
                          fontSize: 14,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                        }}
                      >
                        {b.name} {b.verified && <BadgeCheck size={13} color={C.teal} />}
                      </div>
                      <div style={{ fontSize: 12, color: C.ink2 }}>
                        {b.verified ? 'Verificado' : 'Pendiente'}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: 13.5, color: C.ink2 }}>{b.cat}</span>
                  <span style={{ fontSize: 13.5, color: C.ink2 }}>{b.zone}</span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color:
                        b.plan === 'Destacado'
                          ? C.terra
                          : b.plan === 'Profesional'
                            ? C.teal
                            : C.ink2,
                    }}
                  >
                    {b.plan}
                  </span>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    {!b.verified && (
                      <button
                        onClick={() => toast.success('Negocio verificado ✓')}
                        style={{
                          padding: '6px 11px',
                          borderRadius: 8,
                          border: 'none',
                          background: C.teal,
                          color: '#fff',
                          fontFamily: FB,
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        Verificar
                      </button>
                    )}
                    <button
                      onClick={() => toast.info('Gestionar')}
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 8,
                        border: `1px solid ${C.border}`,
                        background: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ⋯
                    </button>
                  </div>
                </div>
              )
            )}
          </>
        )}

        {tab === 'Suscripciones' && (
          <div style={{ padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
              {[
                ['Gratis', 218, C.ink2],
                ['Profesional', 96, C.teal],
                ['Destacado', 28, C.terra],
              ].map(([name, n, color]) => (
                <div
                  key={name as string}
                  style={{ background: C.grayBg, borderRadius: 14, padding: 18 }}
                >
                  <div style={{ fontSize: 22, marginBottom: 12 }}>👑</div>
                  <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26, marginBottom: 2 }}>
                    {n as number}
                  </div>
                  <div style={{ fontSize: 13, color: color as string, fontWeight: 700 }}>
                    {name as string} · negocios
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── MÉTRICAS ────────────────────────────────────────────────────────────────
function AdminMetrics() {
  const [range, setRange] = useState('Últimos 7 días')
  const ranges = ['Últimos 7 días', 'Últimos 30 días', 'Este mes', 'vs. anterior']
  const big = [
    ['Usuarios activos', '12,480', '+6.2%'],
    ['Retención 30d', '68%', '+2.1%'],
    ['Negocios activos', '342', '+4 nuevos'],
    ['Clics "Cómo llegar"', '48,210', '+12%'],
  ]
  const cats = [
    ['Música en vivo', 88],
    ['Rooftops', 76],
    ['Promociones', 64],
    ['Cultura', 52],
    ['Social', 41],
  ]

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 28px 60px' }}>
      <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26, marginBottom: 20 }}>
        Métricas de la plataforma
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          marginBottom: 24,
        }}
      >
        {ranges.map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              padding: '8px 14px',
              borderRadius: 99,
              flexShrink: 0,
              border: `1px solid ${range === r ? C.teal : C.border}`,
              background: range === r ? C.teal : '#fff',
              color: range === r ? '#fff' : C.ink,
              fontFamily: FB,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {r}
          </button>
        ))}
      </div>

      <div className="admin-4" style={{ marginBottom: 24 }}>
        {big.map(([label, val, delta]) => (
          <div
            key={label as string}
            style={{
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 14,
              padding: 16,
            }}
          >
            <div style={{ fontSize: 12.5, color: C.ink2, marginBottom: 4 }}>{label as string}</div>
            <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 26, marginBottom: 4 }}>
              {val as string}
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: C.success,
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <TrendingUp size={13} /> {delta as string}
            </div>
          </div>
        ))}
      </div>

      <div className="admin-two-col">
        <div>
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 17, marginBottom: 14 }}>
            Actividad de la plataforma
          </div>
          <BarChart />
        </div>
        <div
          style={{
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            padding: 20,
          }}
        >
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 15, marginBottom: 14 }}>
            Categorías más populares
          </div>
          {cats.map(([label, pct]) => (
            <div key={label as string} style={{ marginBottom: 14 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 13.5,
                  marginBottom: 6,
                }}
              >
                <span style={{ fontWeight: 600 }}>{label as string}</span>
                <span style={{ color: C.ink2 }}>{pct as number}%</span>
              </div>
              <div style={{ height: 8, borderRadius: 99, background: C.grayBg }}>
                <div
                  style={{
                    height: '100%',
                    width: `${pct as number}%`,
                    borderRadius: 99,
                    background: C.teal,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Entrada del panel admin ──────────────────────────────────────────────────
export default function AdminDashboard() {
  const raw = localStorage.getItem('px_pending_biz') ?? '[]'
  const pendingCount = (() => {
    try {
      return (JSON.parse(raw) as { status: string }[]).filter((r) => r.status === 'pending').length
    } catch {
      return 0
    }
  })()

  return (
    <AdminShell pendingRequests={pendingCount}>
      <Routes>
        <Route index element={<AdminOverview />} />
        <Route path="registrations" element={<AdminRegistrations />} />
        <Route path="moderation" element={<AdminModeration />} />
        <Route path="manage" element={<AdminManage />} />
        <Route path="metrics" element={<AdminMetrics />} />
      </Routes>
    </AdminShell>
  )
}
